import * as React from 'react';
import { Box, Typography } from '@mui/material';
import {
    eachDayOfInterval,
    startOfWeek,
    endOfWeek,
    format,
    isSameMonth,
    isSameDay,
    parseISO,
} from 'date-fns';
import { MissionLight } from '@wwf/sdk';
import { MissionCalendarItem } from '../MissionCalendarItem';

import { CalendarMode } from '../MissionCalendar';

interface MissionCalendarMonthViewProps {
    missions: MissionLight[];
    rangeStart: Date;
    rangeEnd: Date;
    setMode: (mode: CalendarMode) => void;
    setAnchorDate: (date: Date) => void;
}

export const MissionCalendarMonthView = ({
    missions,
    rangeStart,
    rangeEnd,
    setMode,
    setAnchorDate,
}: MissionCalendarMonthViewProps) => {
    // Generate full calendar grid including padded days
    const calendarStart = startOfWeek(rangeStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(rangeEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
        <Box display="flex" flexDirection="column" height="100%">
            {/* Header row */}
            <Box
                display="flex"
                borderBottom={1}
                borderColor="divider"
                bgcolor="grey.100"
            >
                {weekDays.map(day => (
                    <Box key={day} flex={1} p={1} textAlign="center">
                        <Typography variant="subtitle2" color="textSecondary">
                            {day}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Grid */}
            <Box display="flex" flexWrap="wrap" flex={1}>
                {days.map((day, i) => {
                    const isCurrentMonth = isSameMonth(day, rangeStart);
                    const isToday = isSameDay(day, new Date());

                    // Find missions overlapping this day
                    const dayMissions = missions.filter(m => {
                        const mStart = parseISO(m.startDate!);
                        const mEnd = m.endDate ? parseISO(m.endDate) : mStart;

                        const dString = format(day, 'yyyy-MM-dd');
                        const sString = format(mStart, 'yyyy-MM-dd');
                        const eString = format(mEnd, 'yyyy-MM-dd');

                        return dString >= sString && dString <= eString;
                    });

                    return (
                        <Box
                            key={i}
                            onClick={() => {
                                setAnchorDate(day);
                                setMode('day');
                            }}
                            sx={{
                                width: '14.28%', // 100/7
                                borderRight: (i + 1) % 7 === 0 ? 0 : 1,
                                borderBottom: 1,
                                borderColor: 'divider',
                                minHeight: 120,
                                p: 0.5,
                                bgcolor: isCurrentMonth
                                    ? 'background.paper'
                                    : 'grey.50',
                                opacity: isCurrentMonth ? 1 : 0.6,
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                mb={0.5}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        bgcolor: isToday
                                            ? 'primary.main'
                                            : 'transparent',
                                        color: isToday
                                            ? 'primary.contrastText'
                                            : 'text.primary',
                                        fontWeight: isToday ? 'bold' : 'normal',
                                    }}
                                >
                                    {format(day, 'd')}
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                            >
                                {dayMissions.map(m => (
                                    <MissionCalendarItem
                                        key={m.id}
                                        mission={m}
                                        viewMode="month"
                                    />
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
